/* eslint-disable @typescript-eslint/no-explicit-any */
/*
 * File: useGladysTheCowService.ts
 * Project: papa-bear
 * -----
 * Last Modified: 30 Nov 2021 4:54:52 pm
 * -----
 * Copyright (c) 2021 Elegant Software Solutions All rights reserved.
 */

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { encodeValue } from 'common/helpers/encodeValue';
import lodash from 'lodash';
import { FreightTermsModel } from 'models/FreightTermsModel';
import { ModeOfDeliveryModel } from 'models/ModeOfDeliveryModel';
import { ReduxState } from 'redux/rootReducer';

/* ****************** Interfaces ****************** */
export interface GetFreightTermsParams {
  dataAreaId?: string;
  deliveryTermContains?: string;
  offset: number;
}

export interface GetFreightTermsResponseModel {
  data: FreightTermsModel[];
  totalCount: number;
}

export interface GetModesOfDeliveryParams {
  dataAreaId?: string;
  modeOfDeliveryContains?: string;
  offset: number;
}

export interface GetModesOfDeliveryResponseModel {
  data: ModeOfDeliveryModel[];
  totalCount: number;
}

/* ****************** Variables ****************** */
const gladysTheCowBaseUrl = process.env.REACT_APP_GLADYS_THE_COW_BASE_URL;
const functionsKey = process.env.REACT_APP_API_HOST_KEY_GLADYS_THE_COW;

const baseQuery = fetchBaseQuery({
  baseUrl: gladysTheCowBaseUrl,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as ReduxState).app.accessToken;

    if (token) {
      headers.set('authorization', `Bearer ${token}`);
      headers.set('x-functions-key', functionsKey);
      headers.set('Content-Type', 'application/json');
    }

    return headers;
  }
});

export const deliveryApi = createApi({
  reducerPath: 'deliveryApi',
  baseQuery,
  endpoints: (builder) => ({
    getFreightTerms: builder.query<GetFreightTermsResponseModel, GetFreightTermsParams>({
      query: ({ dataAreaId, offset, deliveryTermContains }) => ({
        url: `deliveryTerms`,
        params: { dataAreaId: dataAreaId ? encodeValue({ isUrl: false, value: dataAreaId }) : '', offset, deliveryTermContains }
      }),
      onCacheEntryAdded: async (_arg, _api) => {
        try {
          await _api.cacheDataLoaded;
          const { originalArgs } = _api.getCacheEntry();
          const { queries } = _api.getState().deliveryApi;
          const lastQueryArgs = { ...originalArgs, offset: _arg.offset - 25 };
          const lastQueryData = Object.values(queries).find((value) => lodash.isEqual(value?.originalArgs, lastQueryArgs));

          if (lastQueryData) {
            _api.updateCachedData((draft) => {
              draft.data.unshift(...(lastQueryData.data as any).data);
            });
          }
        } catch (error) {
          console.log(error);
        }
      }
    }),
    getFreightTermsByName: builder.query<GetFreightTermsResponseModel, GetFreightTermsParams>({
      query: ({ dataAreaId, deliveryTermContains, offset }) => ({
        url: 'deliveryTerms',
        params: { dataAreaId: dataAreaId ? encodeValue({ isUrl: false, value: dataAreaId }) : '', deliveryTermContains, offset }
      })
    }),
    getModesOfDelivery: builder.query<GetModesOfDeliveryResponseModel, GetModesOfDeliveryParams>({
      query: ({ dataAreaId, offset }) => ({
        url: `modesOfDelivery`,
        params: { dataAreaId: dataAreaId ? encodeValue({ isUrl: false, value: dataAreaId }) : '', offset }
      }),
      onCacheEntryAdded: async (_arg, _api) => {
        try {
          await _api.cacheDataLoaded;
          const { originalArgs } = _api.getCacheEntry();
          const { queries } = _api.getState().deliveryApi;
          const lastQueryArgs = { ...originalArgs, offset: _arg.offset - 25 };
          const lastQueryData = Object.values(queries).find((value) => lodash.isEqual(value?.originalArgs, lastQueryArgs));

          if (lastQueryData) {
            _api.updateCachedData((draft) => {
              draft.data.unshift(...(lastQueryData.data as any).data);
            });
          }
        } catch (error) {
          console.log(error);
        }
      }
    }),
    getModesOfDeliveryByName: builder.query<GetModesOfDeliveryResponseModel, GetModesOfDeliveryParams>({
      query: ({ dataAreaId, modeOfDeliveryContains, offset }) => ({
        url: 'modesOfDelivery',
        params: { dataAreaId: dataAreaId ? encodeValue({ isUrl: false, value: dataAreaId }) : '', modeOfDeliveryContains, offset }
      })
    }),
    getVersion: builder.query<string, void>({
      query: () => ({
        url: '/diagnostics/version',
        responseHandler: (response): Promise<string> => response.text()
      })
    }),
    getApiName: builder.query<string, void>({
      query: () => ({
        url: '/diagnostics/apiName',
        responseHandler: (response): Promise<string> => response.text()
      })
    })
  })
});

export const {
  useGetFreightTermsQuery,
  useGetFreightTermsByNameQuery,
  useLazyGetFreightTermsByNameQuery,
  useGetModesOfDeliveryQuery,
  useGetModesOfDeliveryByNameQuery,
  useLazyGetModesOfDeliveryByNameQuery,
  useGetVersionQuery,
  useGetApiNameQuery
} = deliveryApi;
